/**
 * Logic hiển thị sản phẩm dùng chung cho `apps/admin` (preview) và `apps/web` (thật).
 *
 * Nguồn chân lý của **tính toán** giảm giá là `apps/api/src/lib/pricing.ts` — API đã
 * trả sẵn `onSale`, `salePrice`, `discountPercent` trong JSON. File này chỉ:
 *   1. Định nghĩa **nhãn** (mask góc phải ảnh) một chỗ, để admin và web vẽ giống nhau.
 *   2. Cung cấp `computeSalePrice()` để admin xem trước giá **trước khi lưu** (chưa có
 *      JSON từ API). Công thức phải khớp `pricing.ts` — đổi một nơi là đổi cả hai.
 */
import type { Product, ProductDiscount, ProductSpecs } from './types'

// ─────────────────────────────────────────────────────────────────────────────
// Giảm giá
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Giá sau giảm, **làm tròn về nghìn đồng** để không ra số lẻ kiểu 44.550₫.
 * Giá quá nhỏ (dưới 10.000) thì làm tròn về trăm để không bị về 0.
 * Khớp `applyDiscount()` ở `apps/api/src/lib/pricing.ts`.
 */
export function computeSalePrice(price: number, percent: number): number {
  if (!Number.isFinite(price) || price <= 0 || percent <= 0) return price
  const raw = (price * (100 - percent)) / 100
  const unit = price < 10_000 ? 100 : 1000
  return Math.max(0, Math.round(raw / unit) * unit)
}

/** Giảm giá có đang hiệu lực tại thời điểm `now` không (khớp `isSaleActive()` ở API). */
export function isDiscountActive(discount: ProductDiscount | null | undefined, now: Date = new Date()): boolean {
  if (!discount || !discount.percent || discount.percent <= 0) return false
  const t = now.getTime()
  if (discount.startsAt && new Date(discount.startsAt).getTime() > t) return false
  if (discount.endsAt && new Date(discount.endsAt).getTime() < t) return false
  return true
}

// ─────────────────────────────────────────────────────────────────────────────
// Nhãn (mask góc phải ảnh)
// ─────────────────────────────────────────────────────────────────────────────

export type ProductBadgeKey = 'hot' | 'new' | 'sale'
export type ProductBadgeTone = 'danger' | 'primary' | 'warning'

export interface ProductBadge {
  key: ProductBadgeKey
  /** Chữ in trên mask, đã điền số (vd `-20%`). */
  label: string
  /** Tên tone màu — component map sang token, **không** hardcode hex. */
  tone: ProductBadgeTone
}

/**
 * Định nghĩa mask **một chỗ**. Thứ tự trong object = thứ tự ưu tiên hiển thị
 * (hot → new → sale). Mỗi nhãn một màu riêng để nhìn là phân biệt được.
 */
export const PRODUCT_BADGES: Record<ProductBadgeKey, { label: string; tone: ProductBadgeTone }> = {
  hot: { label: 'HOT', tone: 'danger' },
  new: { label: 'MỚI', tone: 'primary' },
  sale: { label: '-{percent}%', tone: 'warning' },
}

type BadgeInput = Pick<Product, 'isHot' | 'isNewArrival'> & {
  /** Từ API (`onSale`) hoặc tự tính ở admin bằng `isDiscountActive()`. */
  onSale?: boolean | null
  discountPercent?: number | null
}

/**
 * Danh sách nhãn cần vẽ cho một sản phẩm, đúng thứ tự ưu tiên.
 * Web và admin cùng gọi hàm này → không bao giờ lệch nhau.
 */
export function productBadges(p: BadgeInput): ProductBadge[] {
  const out: ProductBadge[] = []
  if (p.isHot) out.push({ key: 'hot', ...PRODUCT_BADGES.hot })
  if (p.isNewArrival) out.push({ key: 'new', ...PRODUCT_BADGES.new })
  if (p.onSale && p.discountPercent && p.discountPercent > 0) {
    out.push({
      key: 'sale',
      tone: PRODUCT_BADGES.sale.tone,
      label: PRODUCT_BADGES.sale.label.replace('{percent}', String(p.discountPercent)),
    })
  }
  return out
}

// ─────────────────────────────────────────────────────────────────────────────
// Thông số (kích thước · trọng lượng)
// ─────────────────────────────────────────────────────────────────────────────

/** `4.5` → `"4,5"`, `5` → `"5"`. Dấu thập phân tiếng Việt là dấu phẩy. */
function num(v: number): string {
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 }).format(v)
}

const ok = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0

/**
 * `"5 × 3 × 2 cm"`, hoặc `null` khi **thiếu bất kỳ chiều nào**.
 *
 * ⚠️ Cố ý **không** in được một phần: `"5 × 2 cm"` với ba chiều mà chỉ điền hai thì khách không
 * biết cái nào thiếu, và mỗi sản phẩm lại thiếu một chiều khác nhau. Thà không có dòng đó.
 */
export function formatDimensions(specs: ProductSpecs | null | undefined): string | null {
  if (!specs) return null
  const { lengthCm: l, widthCm: w, heightCm: h } = specs
  if (!ok(l) || !ok(w) || !ok(h)) return null
  return `${num(l)} × ${num(w)} × ${num(h)} cm`
}

/** `"28 g"`, hoặc `null` khi chưa điền. */
export function formatWeight(specs: ProductSpecs | null | undefined): string | null {
  const g = specs?.weightG
  return ok(g) ? `${num(g)} g` : null
}
